import {
  AssetStatus,
  DatasetRow,
  DatasetRowStrategy,
  type Dataset,
  type MessageDefinition,
  type MessageTemplate,
  type ParameterSet,
  type ResolvedMessageTemplate,
  type RuntimeMessageContext,
} from '@k-ucap/shared';
import type { ExpressionResolverService } from './ExpressionResolverService.js';

export interface ResolveOptions {
  parameterSets?: ParameterSet[];
  dataset?: Dataset;
  datasetRow?: DatasetRow;
  runtimeOverrides?: Record<string, unknown>;
  context?: RuntimeMessageContext;
  messageDefinition?: MessageDefinition;
}

export interface TemplateResolverService {
  resolve(
    template: MessageTemplate,
    base: MessageTemplate | undefined,
    options: ResolveOptions
  ): ResolvedMessageTemplate;
  applyInheritance(template: MessageTemplate, base: MessageTemplate | undefined): MessageTemplate;
  validateResolved(resolved: ResolvedMessageTemplate): ResolvedMessageTemplate;
}

export const createTemplateResolverService = (
  expressionResolver: ExpressionResolverService
): TemplateResolverService => {
  const resolveValue = (
    value: unknown,
    context: RuntimeMessageContext,
    parameterSets: ParameterSet[],
    datasetRow?: DatasetRow
  ): unknown => {
    if (typeof value === 'string') {
      return expressionResolver.resolve(value, {
        sequence: context.sequence,
        timestamp: context.timestamp,
        runId: context.runId,
        sessionId: context.sessionId,
        vehicleId: context.vehicleId,
        randomSeed: context.randomSeed,
        datasetRow,
        parameterSet: parameterSets.reduce((acc, ps) => ({ ...acc, ...ps.values }), {}),
      });
    }
    return value;
  };

  const applyParameterSets = (
    payload: Record<string, unknown>,
    parameterSets: ParameterSet[],
    context: RuntimeMessageContext
  ): Record<string, unknown> => {
    const result = { ...payload };
    for (const ps of parameterSets) {
      if (ps.status === AssetStatus.ARCHIVED) continue;
      for (const [key, value] of Object.entries(ps.values)) {
        result[key] = resolveValue(value, context, parameterSets);
      }
    }
    return result;
  };

  const applyDatasetRow = (
    payload: Record<string, unknown>,
    dataset: Dataset | undefined,
    datasetRow: DatasetRow | undefined,
    context: RuntimeMessageContext,
    parameterSets: ParameterSet[]
  ): Record<string, unknown> => {
    if (!dataset || !datasetRow) return payload;
    const result = { ...payload };
    for (const binding of dataset.bindings) {
      if (binding.datasetId !== dataset.id) continue;
      const rawValue = datasetRow[binding.column];
      const resolved = resolveValue(rawValue, context, parameterSets, datasetRow);
      result[binding.fieldKey] =
        resolved === null || resolved === undefined ? (binding.defaultValue ?? null) : resolved;
    }
    return result;
  };

  const applyRuntimeOverrides = (
    payload: Record<string, unknown>,
    overrides: Record<string, unknown> | undefined,
    context: RuntimeMessageContext,
    parameterSets: ParameterSet[]
  ): Record<string, unknown> => {
    if (!overrides) return payload;
    const result = { ...payload };
    for (const [key, value] of Object.entries(overrides)) {
      result[key] = resolveValue(value, context, parameterSets);
    }
    return result;
  };

  return {
    applyInheritance(template, base) {
      if (!base || template.baseTemplateId !== base.id) return template;
      const mergedPayload: Record<string, unknown> = { ...base.payload, ...template.payload };
      const mergedParameterSetIds = Array.from(
        new Set([...(base.parameterSetIds ?? []), ...(template.parameterSetIds ?? [])])
      );
      const mergedDatasetBindings = [...(base.datasetBindings ?? []), ...(template.datasetBindings ?? [])];
      return {
        ...base,
        ...template,
        payload: mergedPayload,
        parameterSetIds: mergedParameterSetIds,
        datasetBindings: mergedDatasetBindings,
      };
    },

    resolve(template, base, options) {
      const merged = base ? this.applyInheritance(template, base) : template;
      const initialPayload = { ...merged.payload };
      const context = options.context ?? {
        sequence: 1,
        timestamp: new Date().toISOString(),
      };
      const parameterSets = options.parameterSets ?? [];
      const datasetRow = options.datasetRow;

      let resolved = applyParameterSets(initialPayload, parameterSets, context);
      resolved = applyDatasetRow(resolved, options.dataset, datasetRow, context, parameterSets);
      resolved = applyRuntimeOverrides(resolved, options.runtimeOverrides, context, parameterSets);

      for (const key of Object.keys(resolved)) {
        resolved[key] = resolveValue(resolved[key], context, parameterSets, datasetRow);
      }

      const appliedSources: ResolvedMessageTemplate['appliedSources'] = [
        { source: 'template', id: template.id },
        ...(base ? [{ source: 'baseTemplate', id: base.id }] : []),
        ...parameterSets.map((ps) => ({ source: 'parameterSet', id: ps.id })),
      ];
      if (datasetRow) {
        appliedSources.push({ source: 'datasetRow', id: 'inline' });
      }
      if (options.runtimeOverrides && Object.keys(options.runtimeOverrides).length > 0) {
        appliedSources.push({ source: 'runtimeOverride', id: 'manual' });
      }

      const resolvedTemplate: ResolvedMessageTemplate = {
        templateId: merged.id,
        messageType: merged.messageType,
        messageDefinitionId: merged.messageDefinitionId,
        resolvedPayload: resolved,
        appliedSources,
      };
      return this.validateResolved(resolvedTemplate);
    },

    validateResolved(resolved) {
      const issues: string[] = [];
      if (!resolved.resolvedPayload || Object.keys(resolved.resolvedPayload).length === 0) {
        issues.push('Resolved payload is empty');
      }
      if (resolved.messageType === undefined || resolved.messageType === null) {
        issues.push('Message type is missing');
      }
      return {
        ...resolved,
        validationResult: issues.length > 0 ? { severity: 'WARN', issues } : undefined,
      };
    },
  };
};

export const createDatasetRowSelector = () => {
  const selectRow = (dataset: Dataset, strategy: DatasetRowStrategy, index: number): DatasetRow | undefined => {
    const rows = dataset.rows ?? dataset.previewRows ?? [];
    if (rows.length === 0) return undefined;
    switch (strategy) {
      case DatasetRowStrategy.FIRST:
        return rows[0];
      case DatasetRowStrategy.SEQUENTIAL:
        return rows[index % rows.length];
      case DatasetRowStrategy.RANDOM: {
        const idx = Math.floor(Math.random() * rows.length);
        return rows[idx];
      }
      case DatasetRowStrategy.LOOP:
        return rows[index % rows.length];
      default:
        return rows[0];
    }
  };
  return { selectRow };
};
